import {
  Button as InitialButton,
  ToggleButton,
  Chip as FrontileChip,
  ButtonGroup,
} from '@frontile/buttons';
import type { ButtonArgs as InitialButtonArgs, ChipSignature } from '@frontile/buttons';
import type { TOC } from '@ember/component/template-only';
import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { Popover } from './overlays.gts';
import { PhArrowsClockwise } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { and, eq } from '../utilities.ts';
import type { PopoverSignature } from '@frontile/overlays/components/popover';

export interface ButtonArgs {
  Args: InitialButtonArgs & {
    isRunning?: boolean;
    // Wether or not to show the info button for a popover description.
    showDescription?: boolean;
    // The description to show in the popover, but also used for screen readers.
    description?: string;
    // Icon component to display (usually a Phosphor icon)
    icon?: ComponentLike;
    // Text label for the button
    label?: string;
    // Icon placement relative to label
    iconPlacement?: 'start' | 'end';
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}
/* Updates the implementation of how the button is rendered to include a spinner
   when the button is running. Long term we should look at provider context or
   see if a spinner can be added to the main library. The alternative is to make
   a component that just provides the content inside the button. But we also want
   the button to disable when running. */

const Button: TOC<ButtonArgs> = <template>
  {{#if @showDescription}}
    <Popover @placement="top-end" as |p|>
      <ButtonGroup
        @appearance={{@appearance}}
        @intent={{@intent}}
        @size={{@size}}
        @class={{concat @class " relative"}}
        {{! @glint-ignore: disabled attribute may not be in type depending on resolution context }}
        disabled={{if @isRunning true false}}
        aria-label={{@description}}
        as |g|
      >
        <g.Button @class="rounded-r-none border-r-0" ...attributes>
          <div
            class="transition-opacity duration-500 ease-in-out
              {{if @isRunning 'opacity-100' 'opacity-0 pointer-events-none'}}
              flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          >
            <PhArrowsClockwise @size="24" class="animate-spin" />
          </div>
          <div
            class="transition-opacity duration-500 ease-in-out
              {{if @isRunning 'opacity-0 pointer-events-none' 'opacity-100'}}
              flex items-center justify-center gap-1.5"
          >
            {{#if (and @icon @label)}}
              {{! Icon + Label layout }}
              {{#if (eq @iconPlacement "end")}}
                <span class="flex">{{@label}}</span>
                {{#let @icon as |Icon|}}
                  <Icon />
                {{/let}}
              {{else}}
                {{#let @icon as |Icon|}}
                  <Icon />
                {{/let}}
                <span class="flex">{{@label}}</span>
              {{/if}}
            {{else if @icon}}
              {{! Icon only }}
              {{#let @icon as |Icon|}}
                <Icon />
              {{/let}}
            {{else if @label}}
              {{! Label only }}
              <span class="flex">{{@label}}</span>
            {{else}}
              {{! Default yield block }}
              <span class="flex" aria-hidden="true">{{yield}}</span>
            {{/if}}
          </div>
        </g.Button>
        <g.Button
          @class="w-12 rounded-l-none border-l border-l-gray-300 dark:border-l-gray-600"
          {{p.trigger}}
          {{p.anchor}}
        >?</g.Button>
      </ButtonGroup>
      {{! Leave the faded. This will prevent weird interactions when multiple buttons on screen have info.}}
      <p.Content @class="p-2 prose dark:prose-invert bg-white rounded shadow-lg" @backdrop="faded">
        {{@description}}
      </p.Content>
    </Popover>
  {{else}}
    <InitialButton
      @type={{@type}}
      @appearance={{@appearance}}
      @intent={{@intent}}
      @size={{@size}}
      @isRenderless={{@isRenderless}}
      @class={{concat @class " relative"}}
      @isInGroup={{@isInGroup}}
      disabled={{if @isRunning true false}}
      aria-label={{@description}}
      ...attributes
    >
      <div
        class="transition-opacity duration-500 ease-in-out
          {{if @isRunning 'opacity-100' 'opacity-0 pointer-events-none'}}
          flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      >
        <PhArrowsClockwise @size="24" class="animate-spin" />
      </div>
      <div
        class="transition-opacity duration-500 ease-in-out
          {{if @isRunning 'opacity-0 pointer-events-none' 'opacity-100'}}
          flex items-center justify-center gap-1.5"
      >
        {{#if (and @icon @label)}}
          {{! Icon + Label layout }}
          {{#if (eq @iconPlacement "end")}}
            <span class="flex">{{@label}}</span>
            {{#let @icon as |Icon|}}
              <Icon />
            {{/let}}
          {{else}}
            {{#let @icon as |Icon|}}
              <Icon />
            {{/let}}
            <span class="flex">{{@label}}</span>
          {{/if}}
        {{else if @icon}}
          {{! Icon only }}
          {{#let @icon as |Icon|}}
            <Icon />
          {{/let}}
        {{else if @label}}
          {{! Label only }}
          <span class="flex">{{@label}}</span>
        {{else}}
          {{! Default yield block }}
          <span class="flex" aria-hidden="true">{{yield}}</span>
        {{/if}}
      </div>
    </InitialButton>
  {{/if}}
</template>;

/**
 * TooltipButton - A button with an integrated hover tooltip
 *
 * This component wraps a Button with a Popover that shows on hover,
 * providing a tooltip-like UX without accessibility issues. Features
 * purple hover states and violet tooltips with arrows for a polished look.
 *
 * Can be used as a drop-in replacement for ActionIconButton.
 *
 * @example
 * ```gts
 * <TooltipButton
 *   @tooltip="Click to save"
 *   @icon={{PhFloppyDisk}}
 *   @appearance="minimal"
 *   @intent="default"
 *   @size="sm"
 *   {{on "click" this.save}}
 * />
 * ```
 *
 * @example With active state (for drawer toggles)
 * ```gts
 * <TooltipButton
 *   @tooltip="Comments"
 *   @isActive={{this.isCommentsDrawerOpen}}
 *   @appearance="minimal"
 *   @size="sm"
 *   {{on "click" this.toggleComments}}
 * >
 *   <PhChat @size={{24}} />
 * </TooltipButton>
 * ```
 */
export interface TooltipButtonArgs {
  Args: InitialButtonArgs & {
    /** The tooltip text to display on hover */
    tooltip: string;
    /** Tooltip placement relative to button */
    tooltipPlacement?: PopoverSignature['Args']['placement'];
    /** Icon component to display (usually a Phosphor icon) */
    icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
    /** Size for the icon (default: 24) */
    iconSize?: number | string;
    /** Text label for the button */
    label?: string;
    /** Icon placement relative to label */
    iconPlacement?: 'start' | 'end';
    /** Whether the tooltip is currently disabled (e.g., when a drawer is open) */
    tooltipDisabled?: boolean;
    /** Offset distance from anchor (default: 8) */
    tooltipOffset?: number;
    /** Whether button is in active/selected state (shows purple background) */
    isActive?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

class TooltipButton extends Component<TooltipButtonArgs> {
  get placement() {
    return this.args.tooltipPlacement || 'top';
  }

  get buttonClasses() {
    const baseClasses =
      'group hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-200';
    const activeClasses = this.args.isActive ? ' bg-purple-100 dark:bg-purple-900/30' : '';
    return baseClasses + activeClasses;
  }

  get arrowClasses() {
    // Arrow pointing toward the button based on popover placement
    switch (this.placement) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return 'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rotate-45 bg-violet-900';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return 'absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-violet-900';
      case 'left':
      case 'left-start':
      case 'left-end':
        return 'absolute left-full top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 rotate-45 bg-violet-900';
      case 'right':
      case 'right-start':
      case 'right-end':
        return 'absolute right-full top-1/2 -translate-y-1/2 -mr-1 w-2 h-2 rotate-45 bg-violet-900';
      default:
        return 'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rotate-45 bg-violet-900';
    }
  }

  <template>
    <Popover
      @placement={{this.placement}}
      @offsetOptions={{if @tooltipOffset @tooltipOffset 8}}
      as |p|
    >
      <InitialButton
        @type={{@type}}
        @appearance={{@appearance}}
        @intent={{@intent}}
        @size={{@size}}
        @isRenderless={{@isRenderless}}
        @class={{concat @class " " this.buttonClasses}}
        @isInGroup={{@isInGroup}}
        aria-label={{@tooltip}}
        {{on "mouseenter" p.open}}
        {{on "mouseleave" p.close}}
        {{on "click" p.close}}
        {{p.anchor}}
        ...attributes
      >
        <span
          class="flex items-center justify-center gap-1.5 text-gray-600 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200"
        >
          {{#if (and @icon @label)}}
            {{! Icon + Label layout }}
            {{#if (eq @iconPlacement "end")}}
              <span class="flex">{{@label}}</span>
              {{#let @icon as |Icon|}}
                <Icon @size={{if @iconSize @iconSize 24}} />
              {{/let}}
            {{else}}
              {{#let @icon as |Icon|}}
                <Icon @size={{if @iconSize @iconSize 24}} />
              {{/let}}
              <span class="flex">{{@label}}</span>
            {{/if}}
          {{else if @icon}}
            {{! Icon only }}
            {{#let @icon as |Icon|}}
              <Icon @size={{if @iconSize @iconSize 24}} />
            {{/let}}
          {{else if @label}}
            {{! Label only }}
            <span class="flex">{{@label}}</span>
          {{else}}
            {{! Default yield block }}
            <span class="flex">{{yield}}</span>
          {{/if}}
        </span>
      </InitialButton>
      {{#unless @tooltipDisabled}}
        <p.Content
          @preventAutoFocus={{true}}
          @disableTransitions={{true}}
          @class="bg-violet-900 text-white text-sm font-medium px-3 py-2 rounded-md shadow-lg z-50 overflow-visible text-center whitespace-nowrap"
        >
          {{@tooltip}}
          {{! Arrow pointing toward the button }}
          <div class={{this.arrowClasses}}></div>
        </p.Content>
      {{/unless}}
    </Popover>
  </template>
}

/**
 * Chip wrapper that fixes icon alignment issue in Frontile's Chip component.
 * The Frontile Chip's content slot doesn't properly align icons with text
 * because it uses `flex-1` without `inline-flex items-center`.
 * This wrapper adds the necessary classes to ensure icons stay inline with text.
 */
const Chip: TOC<ChipSignature> = <template>
  <FrontileChip
    @appearance={{@appearance}}
    @intent={{@intent}}
    @size={{@size}}
    @radius={{@radius}}
    @withDot={{@withDot}}
    @onClose={{@onClose}}
    @isDisabled={{@isDisabled}}
    @class={{@class}}
    ...attributes
  >
    <span class="inline-flex items-center gap-1">
      {{yield}}
    </span>
  </FrontileChip>
</template>;

export { Button, Chip, ToggleButton, ButtonGroup, TooltipButton };
export type { ChipSignature };
